import React from 'react';
import PropTypes from 'prop-types';

class AudioRecorder extends React.Component {
  constructor(props) {
    super(props);
    this.mediaRecorder = null;
    this.audioChunks = [];
    this.stream = null;
    this.isUnmounted = false;
    this.audioContext = null;
    this.analyser = null;
    this.volumeCheckInterval = null;
  }

  componentDidUpdate(prevProps) {
    if (prevProps.recording !== this.props.recording) {
      if (this.props.recording) {
        this.startRecording();
      } else {
        this.stopRecording();
      }
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    this.cleanupResources();
  }

  cleanupResources = () => {
    // Limpar interval de verificação de volume
    if (this.volumeCheckInterval) {
      clearInterval(this.volumeCheckInterval);
      this.volumeCheckInterval = null;
    }

    // Parar a gravação se estiver ativa
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try {
        this.mediaRecorder.stop();
      } catch (e) {
        console.warn('Error while stopping media recorder:', e);
      }
    }

    // Fechar AudioContext
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
    }

    // Encerrar a stream de mídia
    if (this.stream) {
      this.stream.getTracks().forEach((track) => {
        track.stop();
        track.enabled = false;
      });
      this.stream = null;
    }

    // Limpar referências
    this.mediaRecorder = null;
    this.audioChunks = [];
    this.audioContext = null;
    this.analyser = null;
  };

  // Função para detectar mime type suportado
  getSupportedMimeType = () => {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/ogg',
      'audio/wav',
      'audio/mp4',
      'audio/mpeg',
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return 'audio/webm'; // fallback
  };

  // Função para verificar se há áudio sendo capturado
  setupVolumeMonitoring = () => {
    if (!this.stream || this.isUnmounted) return;

    try {
      this.audioContext = new (
        window.AudioContext || window.webkitAudioContext
      )();
      this.analyser = this.audioContext.createAnalyser();
      const source = this.audioContext.createMediaStreamSource(this.stream);
      source.connect(this.analyser);

      this.analyser.fftSize = 256;
      const bufferLength = this.analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      // Verificar volume a cada 100ms
      this.volumeCheckInterval = setInterval(() => {
        if (this.isUnmounted || !this.analyser) return;

        this.analyser.getByteFrequencyData(dataArray);
        const volume =
          dataArray.reduce((sum, value) => sum + value, 0) / bufferLength;

        // Se não há volume por muito tempo, pode indicar problema
        if (volume === 0) {
          console.warn('No audio input detected');
        }
      }, 100);
    } catch (error) {
      console.warn('Could not setup volume monitoring:', error);
    }
  };

  startRecording = async () => {
    try {
      const { channelCount, sampleRate } = this.props;

      // CORREÇÃO 1: Constraints mais compatíveis com Windows
      const constraints = {
        audio: {
          channelCount: channelCount || 1,
          sampleRate: sampleRate || 44100,
          // Remover restrições que podem causar problemas no Windows
          autoGainControl: true, // Ativar para melhor compatibilidade
          echoCancellation: true, // Ativar para melhor compatibilidade
          noiseSuppression: true, // Ativar para melhor compatibilidade
          // Adicionar constraints específicas para Windows
          volume: 1.0,
          latency: 0.01,
        },
        video: false,
      };

      // Obter a stream de mídia
      this.stream = await navigator.mediaDevices.getUserMedia(constraints);

      // Verificar se o componente ainda está montado
      if (this.isUnmounted) {
        this.cleanupResources();
        return;
      }

      // CORREÇÃO 2: Usar mime type suportado automaticamente
      const mimeType = this.getSupportedMimeType();
      console.log('Using mime type:', mimeType);

      // CORREÇÃO 3: Verificar se as tracks estão ativas
      const audioTracks = this.stream.getAudioTracks();
      if (audioTracks.length === 0) {
        throw new Error('No audio tracks available');
      }

      // Verificar se a track está ativa
      const audioTrack = audioTracks[0];
      if (!audioTrack.enabled) {
        audioTrack.enabled = true;
      }

      console.log('Audio track settings:', audioTrack.getSettings());
      console.log('Audio track constraints:', audioTrack.getConstraints());

      // CORREÇÃO 4: Configurar MediaRecorder com opções específicas
      const options = {
        mimeType,
        audioBitsPerSecond: 128000, // Bitrate específico para melhor qualidade
      };

      // Verificar se as opções são suportadas
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        console.warn(`Mime type ${mimeType} not supported, using default`);
        delete options.mimeType;
      }

      this.mediaRecorder = new MediaRecorder(this.stream, options);
      this.audioChunks = [];

      // CORREÇÃO 5: Configurar event listeners com mais logs
      this.mediaRecorder.ondataavailable = (event) => {
        console.log('Data available:', event.data.size, 'bytes');
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        if (this.isUnmounted) return;

        console.log('Recording stopped. Chunks:', this.audioChunks.length);

        if (this.audioChunks.length === 0) {
          console.error('No audio data recorded');
          this.props.onStop(null, new Error('No audio data recorded'));
          this.cleanupResources();
          return;
        }

        const audioBlob = new Blob(this.audioChunks, { type: mimeType });
        console.log('Audio blob created:', audioBlob.size, 'bytes');

        this.props.onStop(audioBlob);
        this.cleanupResources();
      };

      this.mediaRecorder.onerror = (event) => {
        if (this.isUnmounted) return;

        console.error('Recording error:', event.error);
        this.props.onStop(null, event.error);
        this.cleanupResources();
      };

      this.mediaRecorder.onstart = () => {
        console.log('Recording started');
        // Setup volume monitoring para debug
        this.setupVolumeMonitoring();
      };

      // CORREÇÃO 6: Iniciar com timeslice para garantir dados
      this.mediaRecorder.start(1000); // Coleta dados a cada 1 segundo

      // Chamar onStart após iniciar com sucesso
      if (this.props.onStart) {
        this.props.onStart();
      }
    } catch (error) {
      if (this.isUnmounted) return;

      console.error('Error accessing microphone:', error);

      // CORREÇÃO 7: Mensagens de erro mais específicas
      let errorMessage = 'Error accessing microphone';
      if (error.name === 'NotAllowedError') {
        errorMessage =
          'Microphone access denied. Please allow microphone access and try again.';
      } else if (error.name === 'NotFoundError') {
        errorMessage =
          'No microphone found. Please check your microphone connection.';
      } else if (error.name === 'NotReadableError') {
        errorMessage = 'Microphone is being used by another application.';
      }

      this.props.onStop(null, new Error(errorMessage));
      this.cleanupResources();
    }
  };

  stopRecording = () => {
    console.log('Stopping recording...');
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
  };

  render() {
    return null;
  }
}

AudioRecorder.propTypes = {
  recording: PropTypes.bool.isRequired,
  onStart: PropTypes.func,
  onStop: PropTypes.func.isRequired,
  mimeType: PropTypes.string,
  channelCount: PropTypes.number,
  sampleRate: PropTypes.number,
};

AudioRecorder.defaultProps = {
  mimeType: 'audio/webm',
  channelCount: 1,
  sampleRate: 44100,
};

export default AudioRecorder;
